import React, { useEffect, useRef } from 'react';
import { ConsoleLogMessage } from '../../types';
import './ConsoleLog.css';

interface ConsoleLogProps {
  logs: ConsoleLogMessage[];
  maxLogs?: number;
  onClear?: () => void;
}

const ConsoleLog: React.FC<ConsoleLogProps> = ({ logs, maxLogs = 500, onClear }) => {
  const logEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logEndRef.current) {
      logEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const getLogLevelClass = (level: string): string => {
    switch (level.toLowerCase()) {
      case 'information':
        return 'log-info';
      case 'warning':
        return 'log-warning';
      case 'error':
      case 'critical':
        return 'log-error';
      case 'debug':
        return 'log-debug';
      default:
        return 'log-default';
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  // Limit logs to maxLogs
  const displayedLogs = logs.slice(-maxLogs);

  return (
    <div className="console-log">
      <div className="console-header">
        <h3>Backend Console</h3>
        <div className="console-header-right">
          <span className="log-count">{logs.length} logs</span>
          {onClear && logs.length > 0 && (
            <button className="clear-button" onClick={onClear} title="Clear console">
              Clear
            </button>
          )}
        </div>
      </div>
      <div className="console-content">
        {displayedLogs.length === 0 ? (
          <div className="no-logs">
            <p>Waiting for backend logs...</p>
          </div>
        ) : (
          <div className="logs-list">
            {displayedLogs.map((log, index) => (
              <div
                key={`${log.timestamp}-${index}`}
                className={`log-entry ${getLogLevelClass(log.level)}`}
              >
                <span className="log-time">{formatTime(log.timestamp)}</span>
                <span className="log-level">[{log.level}]</span>
                <span className="log-message">{log.message}</span>
                {log.exception && (
                  <div className="log-exception">
                    <details>
                      <summary>Exception Details</summary>
                      <pre>{log.exception}</pre>
                    </details>
                  </div>
                )}
              </div>
            ))}
            <div ref={logEndRef} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ConsoleLog;
